import { useCallback, useEffect, useState } from "react";
import { Drop, Bed, Coffee, PersonSimpleWalk, Phone, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ICON = { restroom: Coffee, water: Drop, call: Phone, nap: Bed, stretch: PersonSimpleWalk };

export const Therapy = () => {
  const [data, setData] = useState(null);
  const [said, setSaid] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/wellbeing/plans").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (lookup_key) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/wellbeing/subscribe", { lookup_key, origin_url: window.location.origin });
      window.location.href = d.checkout_url;
    } catch (e) { oops(e); setBusy(false); }
  };

  const talk = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/wellbeing/therapy/session", { said });
      setAnswer(d); setSaid(""); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const cancel = async () => {
    try { await api.post("/wellbeing/subscription/cancel"); toast.success("Plan cancelled"); load(); } catch (e) { oops(e); }
  };

  if (!data) return <p className="py-8 text-center text-sm text-slate-500">Loading the plans…</p>;

  return (
    <div className="space-y-4" data-testid="therapy-tab">
      {data.subscription ? (
        <div className="border-l-2 border-[#0B5C2E] bg-[#EAF6F0] px-4 py-3" data-testid="therapy-subscription">
          <p className="overline text-[#0B5C2E]">Your plan</p>
          <p className="mt-1 text-base font-semibold text-[#0F172A]">{data.subscription.name}</p>
          <p className="text-sm text-slate-600">
            {data.subscription.sessions_left} sessions left · paid by {data.subscription.paid_by}
          </p>
          <Button className="mt-2" size="sm" variant="outline" data-testid="therapy-cancel" onClick={cancel}>Cancel plan</Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3" data-testid="therapy-plans">
          {data.plans.map((p, i) => (
            <div key={p.lookup_key} data-testid={`therapy-plan-${i}`} className="border border-border bg-white p-4">
              <p className="font-semibold text-[#0F172A]">{p.name}</p>
              <p className="mt-1 text-xl font-bold text-[#0B5C2E]">${p.price}<span className="text-xs font-semibold text-slate-500"> / month</span></p>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {p.includes.map((x, n) => <li key={n}>· {x}</li>)}
              </ul>
              <Button className="mt-3 w-full" data-testid={`therapy-subscribe-${i}`}
                onClick={() => subscribe(p.lookup_key)} disabled={busy}>
                Take this plan
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="border border-border bg-white p-4" data-testid="therapy-coach">
        <p className="overline text-slate-500">The sleep coach — online now</p>
        <Textarea data-testid="therapy-said" className="mt-2" placeholder="What is keeping you up?"
          value={said} onChange={(e) => setSaid(e.target.value)} />
        <Button className="mt-2" data-testid="therapy-talk" onClick={talk} disabled={busy || said.trim().length < 3}>
          <Sparkle size={15} className="mr-1.5" /> {busy ? "Thinking…" : "Talk it through"}
        </Button>
        {answer && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="therapy-answer">
            <p className="text-sm text-[#0F172A]">{answer.read}</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {(answer.plan || []).map((x, n) => <li key={n}>· {x}</li>)}
            </ul>
            {answer.refer && (
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-[#8E1C15]" data-testid="therapy-refer">
                Please see a real clinician about this
              </p>
            )}
            <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">By {answer.engine_label} · not medical advice</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const Breaks = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [team, setTeam] = useState(null);
  const isBoss = user?.role === "admin" || user?.role === "manager";

  const load = useCallback(() => {
    api.get("/wellbeing/breaks").then(({ data: d }) => setData(d)).catch(oops);
    if (isBoss) api.get("/wellbeing/breaks/team").then(({ data: d }) => setTeam(d)).catch(() => {});
  }, [isBoss]);
  useEffect(() => { load(); }, [load]);

  const start = async (kind) => {
    try {
      await api.post("/wellbeing/breaks/start", { kind, glasses: kind === "water" ? 1 : 0 });
      toast.success(`${kind} break started`); load();
    } catch (e) { oops(e); }
  };

  const stop = async () => {
    try {
      const { data: d } = await api.post("/wellbeing/breaks/stop");
      toast[d.over_run ? "warning" : "success"](`${d.minutes} minutes — allowance ${d.minutes_allowed}`);
      load();
    } catch (e) { oops(e); }
  };

  if (!data) return <p className="py-8 text-center text-sm text-slate-500">Loading your breaks…</p>;

  return (
    <div className="space-y-4" data-testid="breaks-tab">
      <div className="grid gap-3 sm:grid-cols-4">
        {[["Breaks today", data.today.total], ["Water", `${data.today.glasses}/${data.today.water_target}`],
          ["Minutes", data.today.minutes], ["Over-runs", data.today.over_runs]].map(([label, value]) => (
          <div key={label} className="border border-border bg-white p-3">
            <p className="overline text-slate-500">{label}</p>
            <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
          </div>
        ))}
      </div>

      {data.on_break ? (
        <div className="flex flex-wrap items-center gap-3 border-l-2 border-[#B77B00] bg-[#FFF8E6] px-4 py-3" data-testid="breaks-on-break">
          <p className="text-sm font-semibold text-[#0F172A]">
            On a {data.on_break.kind} break · allowance {data.on_break.minutes_allowed} min
          </p>
          <Button size="sm" data-testid="breaks-stop" onClick={stop}>I'm back</Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="breaks-start">
          {data.kinds.map((k) => {
            const Icon = ICON[k.key] || Coffee;
            return (
              <Button key={k.key} variant="outline" data-testid={`breaks-start-${k.key}`} onClick={() => start(k.key)}>
                <Icon size={15} className="mr-1.5" /> {k.label} · {k.minutes} min
              </Button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <a href="/wellbeing" target="_blank" rel="noreferrer" data-testid="breaks-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public wellbeing board
        </a>
        <ShareBar url={`${BACKEND}/api/share/wellbeing`} text="Breaks, naps and sleep therapy at work"
          testid="breaks-share" compact instagram />
      </div>

      {data.breaks.length > 0 && (
        <ul className="space-y-2" data-testid="breaks-list">
          {data.breaks.map((b, i) => (
            <li key={b.id} data-testid={`breaks-row-${i}`}
              className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
              <span className="font-semibold text-[#0F172A]">{b.label}</span>
              <span className="text-slate-600">{String(b.started_at).slice(11, 16)} · {b.minutes ?? "running"} min</span>
              {b.over_run && <span className="text-[11px] font-semibold uppercase text-[#8E1C15]">over allowance</span>}
            </li>
          ))}
        </ul>
      )}

      {team && (
        <div className="border border-border bg-white p-4" data-testid="breaks-team">
          <p className="text-sm font-semibold text-[#0F172A]">Who needs a break</p>
          <p className="mt-1 text-sm text-slate-600">
            {team.needs_a_break.length > 0 ? team.needs_a_break.join(", ") : "Everyone on shift has had a break today."}
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {team.people.map((p) => (
              <li key={p.user_id}>{p.name} · {p.total} breaks · {p.glasses}/{p.water_target} water</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
